import { useMemo } from 'react';
import { useBandStore } from '../stores/bandStore';
import { useSettingsStore } from '../stores/settingsStore';
import type { Band } from '../models/types';

const sourceValue = (source: string): number => (source === 'ls' ? 0 : 1);

export const useFilteredBands = (): Band[] => {
  // Optimized selectors - only subscribe to what we need
  const bands = useBandStore((state) => state.bands);
  const searchVal = useBandStore((state) => state.searchVal);
  const featuredBands = useBandStore((state) => state.featuredBands);
  const shouldShowGreat78Artists = useSettingsStore((state) => state.shouldShowGreat78Artists);

  return useMemo(() => {
    const filtered = searchVal === ''
      ? bands.filter((band) => shouldShowGreat78Artists || band.source !== 'great78')
      : bands.filter((band) =>
          band.bandName.toLowerCase().includes(searchVal.toLowerCase()) &&
          (shouldShowGreat78Artists || band.source !== 'great78')
        );

    // Sort to place featured bands at the top
    filtered.sort((a, b) => {
      const bandIdA = `${a.collection}|${a.source}`;
      const bandIdB = `${b.collection}|${b.source}`;
      const aFeatured = featuredBands.has(bandIdA);
      const bFeatured = featuredBands.has(bandIdB);

      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      // Then by source (ls bands come before great78)
      const sourceComparison = sourceValue(a.source) - sourceValue(b.source);
      if (sourceComparison !== 0) return sourceComparison;

      // Finally by order
      return a.order.toLowerCase().localeCompare(b.order.toLowerCase());
    });

    return filtered;
  }, [bands, searchVal, shouldShowGreat78Artists, featuredBands]);
};